import { useQueryClient } from "@tanstack/react-query";

import { FetchError } from "common/fetch/errors";
import { useFetchMutation, useFetchQuery } from "common/queries/hooks";
import { PaymentErrorData } from "common/types/payments";
import { OrderModificationOrder } from "common/types/orders";

import { useInvalidateHomeSummary } from "client/home/hooks/invalidate";

import {
  ClientListOrder,
  IOrder,
  IOrderLookup,
  OrderLookupSearch,
  IOrderPaymentMutate,
  OrderCancelMutate,
  CanceledOrder,
} from "../types";
import { keys, urls } from "./constants";

export const useOrdersQuery = () => {
  return useFetchQuery<ClientListOrder[]>(keys.orderList(), {
    fetch: { url: urls.orders() },
    config: { keepPreviousData: true },
  });
};

export const useOrderQuery = (id: number, { enabled = true }: { enabled?: boolean } = {}) => {
  return useFetchQuery<IOrder>(keys.orderDetail(id), {
    fetch: { url: urls.order(id) },
    config: { enabled },
  });
};

export const useUpdateOrderPaymentMethodMutation = (
  orderId: number,
  {
    onSuccess,
    onError,
  }: {
    onSuccess: () => void;
    onError: (
      error: FetchError<IOrderPaymentMutate, PaymentErrorData>,
      data: IOrderPaymentMutate,
    ) => void;
  },
) => {
  const queryClient = useQueryClient();
  return useFetchMutation<
    IOrderPaymentMutate,
    IOrder,
    IOrder,
    IOrderPaymentMutate,
    FetchError<IOrderPaymentMutate, PaymentErrorData>
  >({
    fetch: (body) => ({ method: "PATCH", url: urls.orderPayment(orderId), body }),
    config: {
      onSuccess: () => {
        queryClient.invalidateQueries(keys.orders());
        onSuccess();
      },
      onError,
    },
  });
};

export const useOrderCancelMutation = (
  orderId: number,
  { onSuccess }: { onSuccess: (order: CanceledOrder) => void },
) => {
  const queryClient = useQueryClient();
  const invalidateHomeSummary = useInvalidateHomeSummary();
  return useFetchMutation<OrderCancelMutate, CanceledOrder>({
    fetch: (body) => ({ method: "PATCH", url: urls.orderCancel(orderId), body }),
    config: {
      onSuccess: (order) => {
        queryClient.invalidateQueries(keys.orders());
        invalidateHomeSummary();
        onSuccess(order);
      },
    },
  });
};

export const useOrderLookupQuery = (search: OrderLookupSearch) => {
  return useFetchQuery<IOrderLookup, OrderLookupSearch>(keys.orderLookupDetail(search), {
    fetch: { url: urls.orderLookup(), body: search },
  });
};

export const useOrderModificationsQuery = (
  orderId: number,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<OrderModificationOrder>(keys.orderModificationDetail(orderId), {
    fetch: { method: "GET", url: urls.orderModifications(orderId) },
    config: { enabled },
  });
};
